import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineOutlined from '@mui/icons-material/ErrorOutlineOutlined';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import moment from 'moment';
import { Fragment, useMemo } from 'react';
import { IBillingBlocker } from 'src/services/billingExportService/types';

const ERROR_TEXT = '#B42318';
const ERROR_BORDER = '#FDA29B';
const ERROR_SURFACE = '#FEF3F2';
const COUNT_SURFACE = '#FEE4E2';

/** Ticket copy (BOPS-440 Mockup 1), with the count the mockup shows. */
export const blockedMessage = (count: number) =>
  `Billing blocker noted. Download stopped. Review the ${count} billing ${
    count === 1 ? 'blocker' : 'blockers'
  }.`;

const formatDay = (day: string | null) => {
  //  `moment(null)` is invalid but `moment(undefined)` is now, so the empty case is caught first.
  const parsed = day ? moment(day, 'YYYY-MM-DD', true) : moment.invalid();
  return parsed.isValid() ? parsed.format('MMM D, YYYY') : '—';
};

/** Content-derived key for test ids and React keys; no list position in it. */
const blockerKey = (blocker: IBillingBlocker) =>
  [
    //  No patient id on the wire yet, so the MRN keeps two patients' rows apart.
    blocker.patientId ?? blocker.mrn ?? 'no-patient',
    blocker.discipline ?? 'no-discipline',
    blocker.dateOfService ?? 'no-date',
    blocker.type ?? 'other',
  ]
    .join('-')
    .replace(/[^a-z0-9-]+/gi, '-')
    .toLowerCase();

//  YYYY-MM-DD, the way report-automation writes dates into a reason.
const ISO_DATE = /(\d{4}-\d{2}-\d{2})/;

/**
 * The reason exactly as report-automation sent it, except that a date in it never wraps at its
 * hyphens ("2026-07-" / "26."). Only the line breaking changes, not the text.
 */
const ReasonText = ({ text }: { text: string }) => (
  <>
    {text.split(ISO_DATE).map((part, index) =>
      ISO_DATE.test(part) ? (
        <Box
          component="span"
          key={index}
          sx={{ whiteSpace: 'nowrap' }}
        >
          {part}
        </Box>
      ) : (
        part
      )
    )}
  </>
);

type TPatientGroup = {
  groupId: string;
  patientName: string | null;
  mrn: string | null;
  blockers: IBillingBlocker[];
};

/** One group per patient, in the order the export reported them; dates ascending within. */
const groupByPatient = (blockers: IBillingBlocker[]): TPatientGroup[] => {
  const groups = new Map<string, TPatientGroup>();
  blockers.forEach((blocker) => {
    const groupId = (blocker.patientId ?? `${blocker.patientName ?? ''}-${blocker.mrn ?? ''}`)
      .replace(/[^a-z0-9-]+/gi, '-')
      .toLowerCase();
    const group = groups.get(groupId) ?? {
      groupId,
      patientName: blocker.patientName,
      mrn: blocker.mrn,
      blockers: [],
    };
    group.blockers.push(blocker);
    groups.set(groupId, group);
  });
  return Array.from(groups.values()).map((group) => ({
    ...group,
    blockers: [...group.blockers].sort((a, b) =>
      (a.dateOfService ?? '').localeCompare(b.dateOfService ?? '')
    ),
  }));
};

type BannerProps = {
  count: number;
  onView: () => void;
};

/** Mockup 1 — sits above the download, which is disabled while any blocker remains. */
export const BillingBlockersBanner = ({ count, onView }: BannerProps) => (
  <Stack
    data-testid="billing-blockers-banner"
    role="alert"
    direction={{ xs: 'column', sm: 'row' }}
    alignItems={{ xs: 'flex-start', sm: 'center' }}
    spacing={1.5}
    sx={{
      border: `1px solid ${ERROR_BORDER}`,
      borderRadius: '12px',
      backgroundColor: ERROR_SURFACE,
      padding: '12px 16px',
    }}
  >
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{ flex: 1 }}
    >
      <ErrorOutlineOutlined sx={{ color: ERROR_TEXT }} />
      <Typography
        variant="subtitle2"
        sx={{ color: ERROR_TEXT }}
      >
        {blockedMessage(count)}
      </Typography>
    </Stack>
    <Button
      data-testid="billing-blockers-view-button"
      variant="contained"
      size="small"
      sx={{ whiteSpace: 'nowrap' }}
      onClick={onView}
    >
      <Typography variant="buttonText">View billing blockers</Typography>
    </Button>
  </Stack>
);

type DialogProps = {
  open: boolean;
  onClose: () => void;
  blockers: IBillingBlocker[];
  /** The closeout period's own dates, for the subtitle. */
  startDate?: string;
  endDate?: string;
};

/**
 * Mockup 2 — every blocker, grouped by patient. No Action column for this iteration (Olivia,
 * 22 Oct standup): the reason says what to fix, and report-automation does not yet send the patient
 * and episode ids a fix link needs.
 */
export const BillingBlockersDialog = (props: DialogProps) => {
  const { open, onClose, blockers, startDate, endDate } = props;
  const groups = useMemo(() => groupByPatient(blockers), [blockers]);

  const start = startDate ? moment(startDate) : moment.invalid();
  const end = endDate ? moment(endDate) : moment.invalid();
  const periodLabel =
    start.isValid() && end.isValid()
      ? `${start.format('MMM D')} – ${end.format('MMM D, YYYY')}`
      : 'this period';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="billing-blockers-dialog-title"
      PaperProps={{ 'data-testid': 'billing-blockers-dialog' } as any}
    >
      <DialogTitle
        id="billing-blockers-dialog-title"
        sx={{ pr: 6 }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
        >
          <Typography variant="h5">Your billing export is blocked</Typography>
          <Box
            data-testid="billing-blockers-count"
            sx={{
              backgroundColor: COUNT_SURFACE,
              color: ERROR_TEXT,
              borderRadius: '999px',
              px: 1.25,
              py: 0.25,
              fontWeight: 600,
              fontSize: '0.875rem',
            }}
          >
            {blockers.length}
          </Box>
        </Stack>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.5 }}
        >
          {`Fix these issues to download your billing export for ${periodLabel}.`}
        </Typography>
        <IconButton
          aria-label="Close"
          data-testid="billing-blockers-close-icon"
          onClick={onClose}
          sx={{ position: 'absolute', right: 16, top: 16 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { backgroundColor: '#F8F9FC', whiteSpace: 'nowrap' } }}>
              <TableCell>Discipline</TableCell>
              <TableCell>Date of service</TableCell>
              <TableCell>Reason</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map((group) => (
              <Fragment key={group.groupId}>
                <TableRow data-testid={`billing-blockers-patient-${group.groupId}`}>
                  <TableCell
                    colSpan={3}
                    sx={{ borderBottom: 'none', pt: 2, pb: 0.5 }}
                  >
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="baseline"
                    >
                      <Typography variant="subtitle2">
                        {group.patientName ?? 'Unknown patient'}
                      </Typography>
                      {group.mrn && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          {`MRN ${group.mrn}`}
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
                {group.blockers.map((blocker, position) => {
                  //  KX is per charge, so one day can carry the same blocker twice; only a
                  //  repeat gets a suffix, the first keeps its plain key.
                  const base = blockerKey(blocker);
                  const repeats = group.blockers
                    .slice(0, position)
                    .filter((earlier) => blockerKey(earlier) === base).length;
                  const rowId = repeats ? `${base}-${repeats + 1}` : base;
                  return (
                    <TableRow
                      key={rowId}
                      data-testid={`billing-blockers-row-${rowId}`}
                    >
                      <TableCell>{blocker.discipline ?? '—'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatDay(blocker.dateOfService)}
                      </TableCell>
                      <TableCell>
                        {blocker.reason ? <ReasonText text={blocker.reason} /> : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          data-testid="billing-blockers-close-button"
          variant="outlined"
          onClick={onClose}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
